import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Container,
    ContentLayout,
    FormField,
    Header,
    Input,
    Modal,
    Select,
    SpaceBetween,
    StatusIndicator,
    StatusIndicatorProps,
    Table,
    Popover,
} from '@cloudscape-design/components';
import { useUser } from '../../context/UserContext';
import { userManagementApi } from '../../client/clients';
import { UserDto, UserGroupManagementDto } from '../../api';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

/**
 * H2: strip Cognito user-pool prefixes ("us-east-1_foSvUKqAm_Google" → "Google").
 * Cognito-managed sub-groups follow the pattern `<region>_<poolId>_<provider>`.
 *
 * The earlier heuristic (`segments[0].includes('-') && segments[0].length <= 12`)
 * over-stripped any 3-segment group name whose first segment happened to contain
 * a dash. Anchor the check to the actual Cognito format so user-defined group
 * names like "ops-team_2024_pilots" are left intact.
 */
const AWS_REGION_RE = /^[a-z]{2}-[a-z]+-\d+$/;            // e.g. us-east-1, eu-west-2
const COGNITO_POOL_ID_RE = /^[A-Za-z0-9]{8,16}$/;          // e.g. foSvUKqAm — short alphanumeric
function cleanGroupName(group: string): string {
    if (!group) return group;
    const segments = group.split('_');
    if (
        segments.length >= 3 &&
        AWS_REGION_RE.test(segments[0]) &&
        COGNITO_POOL_ID_RE.test(segments[1])
    ) {
        return segments.slice(2).join('_');
    }
    return group;
}

/**
 * H1: map Cognito user statuses to user-readable concepts.
 *  - null/undefined  → Federated (typically a Google sign-in user)
 *  - "CONFIRMED"     → success
 *  - "UNCONFIRMED"   → warning
 *  - everything else → info (best-effort fallback)
 */
function statusToIndicator(status?: string | null): { type: StatusIndicatorProps.Type; label: string } {
    // Backend currently serializes a missing user status as the literal string "null"
    // (federated/Google sign-in users have no Cognito user status). Treat that the
    // same as a true null/undefined.
    if (!status || status === 'null' || status === 'undefined') {
        return { type: 'info', label: 'Federated' };
    }
    if (status === 'CONFIRMED') return { type: 'success', label: 'Confirmed' };
    if (status === 'UNCONFIRMED') return { type: 'warning', label: 'Unconfirmed' };
    if (status === 'FORCE_CHANGE_PASSWORD') return { type: 'warning', label: 'Password change required' };
    return { type: 'info', label: status };
}

export const UserManagementPage: React.FC = () => {
    useDocumentTitle('User management');
    const user = useUser();
    const [users, setUsers] = useState<UserDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [groupAction, setGroupAction] = useState<'ADD' | 'REMOVE'>('ADD');
    const [targetGroup, setTargetGroup] = useState('juhsis-admin');
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    useEffect(() => {
        if (user.isAdmin) {
            fetchUsers();
        }
    }, [user.isAdmin]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await userManagementApi.listAllUsers();
            setUsers(response.data);
        } catch (error: any) {
            console.error('Error fetching users:', error);
            if (error.response?.status === 403) {
                setAlert({ type: 'error', message: 'Access denied — admin privileges required.' });
            } else {
                setAlert({ type: 'error', message: 'Failed to fetch users.' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGroupManagement = async () => {
        if (!selectedUser) return;

        const request: UserGroupManagementDto = {
            username: selectedUser.username,
            groupName: targetGroup,
            action: groupAction,
        };

        try {
            const response = await userManagementApi.manageUserGroup(request);
            setAlert({ type: 'success', message: response.data });
            setShowGroupModal(false);
            fetchUsers();
        } catch (error: any) {
            console.error('Error managing user group:', error);
            const errorMessage = error.response?.data || 'Error managing user group';
            setAlert({ type: 'error', message: errorMessage });
        }
    };

    const openGroupModal = (target: UserDto, action: 'ADD' | 'REMOVE') => {
        setSelectedUser(target);
        setGroupAction(action);
        setShowGroupModal(true);
    };

    if (!user.isAdmin) {
        return (
            <ContentLayout header={<Header variant="h1">User management</Header>}>
                <Container>
                    <Alert type="warning">
                        Access denied. You need admin privileges to access this page.
                    </Alert>
                </Container>
            </ContentLayout>
        );
    }

    // H5: build a friendly modal title that names the role and the target user.
    const modalTitle = (() => {
        if (!selectedUser) return '';
        const targetName = selectedUser.fullName || selectedUser.email || selectedUser.username;
        const roleLabel = targetGroup === 'juhsis-admin' ? 'admin role' : 'read-only role';
        if (groupAction === 'ADD') {
            return `Grant ${roleLabel} to ${targetName}`;
        }
        return `Remove ${roleLabel} from ${targetName}`;
    })();

    return (
        <ContentLayout header={<Header variant="h1">User management</Header>}>
            <SpaceBetween size="l">
                {alert && (
                    <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
                        {alert.message}
                    </Alert>
                )}

                <Container>
                    <Table
                        // H6: compact density and avoid wrapping in identity columns.
                        contentDensity="compact"
                        wrapLines={false}
                        columnDefinitions={[
                            // H4: hide raw username/sub by default. Email + Full Name
                            // are the primary identifiers. The username column is
                            // intentionally absent here; bring it back via a column
                            // toggle in a future tranche if needed.
                            {
                                id: 'email',
                                header: 'Email',
                                cell: (item: UserDto) => item.email,
                                sortingField: 'email',
                            },
                            {
                                id: 'fullName',
                                header: 'Full name',
                                cell: (item: UserDto) => item.fullName,
                                sortingField: 'fullName',
                            },
                            {
                                id: 'status',
                                header: 'Status',
                                cell: (item: UserDto) => {
                                    const { type, label } = statusToIndicator(
                                        // H1: when status is missing, treat as federated.
                                        item.userStatus
                                    );
                                    return <StatusIndicator type={type}>{label}</StatusIndicator>;
                                },
                            },
                            {
                                id: 'groups',
                                header: 'Groups',
                                cell: (item: UserDto) => (
                                    <SpaceBetween direction="horizontal" size="xs">
                                        {(item.groups || []).map((group: string) => (
                                            // H2: render a Cloudscape Badge per cleaned-up group label.
                                            <Badge key={group} color="blue">
                                                {cleanGroupName(group)}
                                            </Badge>
                                        ))}
                                    </SpaceBetween>
                                ),
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (item: UserDto) => {
                                    // T4-B: detect self-action by comparing row email to
                                    // the signed-in user email from UserContext.
                                    const isSelf = !!(item.email && item.email === user.email);
                                    return (
                                        <SpaceBetween direction="horizontal" size="xs">
                                            {!item.groups?.includes('juhsis-admin') && (
                                                <Button
                                                    onClick={() => {
                                                        setTargetGroup('juhsis-admin');
                                                        openGroupModal(item, 'ADD');
                                                    }}
                                                >
                                                    Make admin
                                                </Button>
                                            )}
                                            {item.groups?.includes('juhsis-admin') && (
                                                isSelf ? (
                                                    // T4-B: self-lockout — disable with tooltip explanation.
                                                    <Popover
                                                        dismissButton={false}
                                                        position="top"
                                                        size="small"
                                                        triggerType="custom"
                                                        content="You cannot remove your own admin role"
                                                    >
                                                        <Button
                                                            variant="normal"
                                                            disabled
                                                            data-testid="remove-admin-self"
                                                        >
                                                            Remove admin
                                                        </Button>
                                                    </Popover>
                                                ) : (
                                                    <Button
                                                        variant="normal"
                                                        data-testid="remove-admin-other"
                                                        onClick={() => {
                                                            setTargetGroup('juhsis-admin');
                                                            openGroupModal(item, 'REMOVE');
                                                        }}
                                                    >
                                                        Remove admin
                                                    </Button>
                                                )
                                            )}
                                            {!item.groups?.includes('read-only') && (
                                                <Button
                                                    onClick={() => {
                                                        setTargetGroup('read-only');
                                                        openGroupModal(item, 'ADD');
                                                    }}
                                                >
                                                    Make read-only
                                                </Button>
                                            )}
                                            {item.groups?.includes('read-only') && (
                                                <Button
                                                    variant="normal"
                                                    onClick={() => {
                                                        setTargetGroup('read-only');
                                                        openGroupModal(item, 'REMOVE');
                                                    }}
                                                >
                                                    Remove read-only
                                                </Button>
                                            )}
                                        </SpaceBetween>
                                    );
                                },
                            },
                        ]}
                        items={users}
                        loading={loading}
                        loadingText="Loading users..."
                        sortingDisabled={false}
                        empty={
                            <Box textAlign="center" color="inherit">
                                <b>No users found</b>
                                <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                                    No users to display.
                                </Box>
                            </Box>
                        }
                        header={
                            <Header
                                counter={`(${users.length})`}
                                actions={
                                    <Button
                                        iconName="refresh"
                                        onClick={fetchUsers}
                                        loading={loading}
                                    >
                                        Refresh
                                    </Button>
                                }
                            >
                                Users
                            </Header>
                        }
                    />
                </Container>

                <Modal
                    visible={showGroupModal}
                    onDismiss={() => setShowGroupModal(false)}
                    header={modalTitle}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setShowGroupModal(false)}>
                                    Cancel
                                </Button>
                                <Button variant="primary" onClick={handleGroupManagement}>
                                    {groupAction === 'ADD' ? 'Grant role' : 'Remove role'}
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween size="m">
                        <Box>
                            {/* H5: body copy references the role, not the group name. */}
                            {groupAction === 'ADD'
                                ? `This will grant ${selectedUser?.fullName || selectedUser?.email || 'the user'} the ${targetGroup === 'juhsis-admin' ? 'admin' : 'read-only'} role.`
                                : `This will revoke the ${targetGroup === 'juhsis-admin' ? 'admin' : 'read-only'} role from ${selectedUser?.fullName || selectedUser?.email || 'the user'}.`}
                        </Box>
                        <FormField label="User">
                            <Input value={selectedUser?.email || selectedUser?.username || ''} disabled />
                        </FormField>
                        <FormField label="Role">
                            <Select
                                selectedOption={{
                                    label: targetGroup === 'juhsis-admin' ? 'Admin' : 'Read-only',
                                    value: targetGroup,
                                }}
                                onChange={({ detail }) => setTargetGroup(detail.selectedOption.value!)}
                                options={[
                                    { label: 'Admin', value: 'juhsis-admin' },
                                    { label: 'Read-only', value: 'read-only' },
                                ]}
                            />
                        </FormField>
                    </SpaceBetween>
                </Modal>
            </SpaceBetween>
        </ContentLayout>
    );
};
